import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import Select from "react-select";

import classNames from "classnames";
import invariant from "tiny-invariant";

import { defaultDateFormat } from "../../../../../../lib/utils";
import { ChangeProductModalProps } from "../../types/order";

const ChangeProductModal = ({
    handleClose,
    handleSubmit,
    masterTournaments,
    product,
    products,
    show,
    tournaments,
}: ChangeProductModalProps): React.JSX.Element => {
    const [newTournament, setNewTournament] = useState<number>(0);
    return (
        <Modal show={show} onHide={() => handleClose("changeProduct")}>
            <Modal.Header>
                <Modal.Title className={classNames("text-center")}>
                    Move team to new tournament
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <h6>Current Registration Info:</h6>
                <dl className={classNames("inline")}>
                    <dt>Tournament:</dt>
                    <dd>{product.description}</dd>
                    {product.meta.teamName && (
                        <>
                            <dt>Team:</dt>
                            <dd>{product.meta.teamName}</dd>
                        </>
                    )}
                </dl>
                <p className={classNames("mb-0")}>
                    Select a new tournament from the list below to move this
                    team to that tournament:
                </p>
                <Select
                    isSearchable={true}
                    isClearable={true}
                    className={classNames("mb-4")}
                    options={[...products]
                        .filter(p => p.id !== product.id)
                        .sort((a, b) => {
                            const startDateA = masterTournaments.find(mt => {
                                return tournaments.find(
                                    t =>
                                        t.parent === mt.id &&
                                        t.id === a.meta.tournamentId,
                                );
                            })?.startDate;

                            const startDateB = masterTournaments.find(mt => {
                                return tournaments.find(
                                    t =>
                                        t.parent === mt.id &&
                                        t.id === b.meta.tournamentId,
                                );
                            })?.startDate;

                            return startDateA && startDateB
                                ? new Date(startDateA).getTime() -
                                      new Date(startDateB).getTime()
                                : 0;
                        })
                        .map(p => {
                            const startDate = masterTournaments.find(mt => {
                                return tournaments.find(
                                    t =>
                                        t.parent === mt.id &&
                                        t.id === p.meta.tournamentId,
                                );
                            })?.startDate;

                            const endDate = masterTournaments.find(mt => {
                                return tournaments.find(
                                    t =>
                                        t.parent === mt.id &&
                                        t.id === p.meta.tournamentId,
                                );
                            })?.endDate;
                            invariant(startDate);
                            invariant(endDate);
                            return {
                                value: p.id,
                                label: `${p.description} (${defaultDateFormat.format(new Date(startDate))} - ${defaultDateFormat.format(new Date(endDate))})`,
                            };
                        })}
                    onChange={e => {
                        setNewTournament(e?.value || 0);
                    }}
                />
                {newTournament !== 0 && (
                    <>
                        <h6
                            className={classNames("text-danger", "text-center")}
                        >
                            Are you sure you want to move this team to the
                            following tournament:
                        </h6>
                        <dl className={classNames("inline")}>
                            <dt>Tournament:</dt>
                            <dd>
                                {
                                    products.find(p => p.id === newTournament)
                                        ?.description
                                }
                            </dd>
                            {product.meta.teamName && (
                                <>
                                    <dt>Team:</dt>
                                    <dd>{product.meta.teamName}</dd>
                                </>
                            )}
                        </dl>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="primary"
                    onClick={() => handleClose("changeProduct")}
                >
                    No
                </Button>
                <Button
                    variant="danger"
                    disabled={newTournament === 0}
                    onClick={() => {
                        invariant(product.meta.teamId);
                        handleSubmit(
                            "changeProduct",
                            () => handleClose("changeProduct"),
                            {
                                oldProductId: product.id,
                                productId: newTournament,
                                teamId: product.meta.teamId,
                            },
                        );
                    }}
                >
                    Yes
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ChangeProductModal;
